import { useCreateSublocation, useSublocations } from '@/sublocation/api'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Building2, Plus, ArrowDownToLine, ArrowUpFromLine, Cog, MoveVertical } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { sublocationFormSchema } from '@/installFlow/schemas'
import { StepFrame } from '@/components/installFlow/StepFrame'
import { StepHeader } from '@/components/installFlow/StepHeader'
import { StepFooter } from '@/components/installFlow/StepFooter'
import * as z from 'zod'
import { useInstallFlowNavigation } from '@/installFlow/useInstallFlowNavigation'
import { useStepSearch } from '@/installFlow/useStepSearch'

const PREDEFINED_SUBLOCATIONS = [
  { name: 'Basement', icon: ArrowDownToLine },
  { name: 'Rooftop', icon: ArrowUpFromLine },
  { name: 'Mechanical Room', icon: Cog },
  { name: 'Elevator Room', icon: MoveVertical },
]

/**
 * Inner component that fetches and displays sublocation list
 * Separated to isolate the suspending useSublocations call
 */
function SublocationList({
  locationId,
  onSelect
}: {
  locationId: number
  onSelect: (sublocation: any) => void
}) {
  const { data: { results: sublocations } = { results: [] } } = useSublocations(locationId)

  return (
    <div className="space-y-4">
      {sublocations.map((sublocation) => (
        <Card
          key={sublocation.id}
          className="p-4 hover:border-primary cursor-pointer transition-colors"
          onClick={() => onSelect(sublocation)}
          data-testid={`sublocation-card-${sublocation.id}`}
        >
          <div className="flex items-start">
            <Building2 className="h-5 w-5 mr-3 mt-0.5 text-muted-foreground" />
            <div className="flex-1">
              <h3 className="font-medium">{sublocation.name}</h3>
              {sublocation.installations && sublocation.installations.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {sublocation.installations.length} asset{sublocation.installations.length === 1 ? "" : "s"}
                </p>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}

export function SublocationStep() {
  const { goToStep, goToPreviousStep } = useInstallFlowNavigation()
  const search = useStepSearch('sublocation')
  const locationId = search.locationId
  const installationName = search.installationName

  const createSublocation = useCreateSublocation()
  const [showForm, setShowForm] = useState(false)

  const form = useForm<z.infer<typeof sublocationFormSchema>>({
    resolver: zodResolver(sublocationFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
    },
  })

  const handleSelectSublocation = (sublocation: any) => {
    goToStep('details', {
      sublocationId: sublocation.id,
      ...(installationName && { installationName }), // Pass through if creating new installation
    })
  }

  const handleCreateSublocation = async (values: z.infer<typeof sublocationFormSchema>) => {
    try {
      // Create sublocation on server (SAVED, not draft)
      const newSublocation = await createSublocation.mutateAsync({
        name: values.name,
        location: locationId,
      })

      // Navigate directly with new sublocationId in URL
      // No draft needed - entity is now saved on server
      form.reset()
      setShowForm(false)
      goToStep('details', {
        sublocationId: newSublocation.id,
        ...(installationName && { installationName }), // Pass through if creating new installation
      })
    } catch (error) {
      console.error('Failed to create sublocation:', error)
    }
  }

  const handleSelectPredefinedSublocation = async (name: string) => {
    try {
      // Create sublocation on server (SAVED, not draft)
      const newSublocation = await createSublocation.mutateAsync({
        name,
        location: locationId,
      })

      // Navigate directly with new sublocationId in URL
      goToStep('details', {
        sublocationId: newSublocation.id,
        ...(installationName && { installationName }), // Pass through if creating new installation
      })
    } catch (error) {
      console.error('Failed to create sublocation:', error)
    }
  }

  // Guard against missing locationId
  if (!locationId) {
    return null
  }

  if (showForm) {
    return (
      <StepFrame
        header={
          <StepHeader
            title="Where is this equipment located?"
            description="Choose a sublocation so teams can find it quickly on site."
            onBack={() => setShowForm(false)}
          />
        }
        main={
          <div className="space-y-4">
            {/* Predefined sublocation grid */}
            <div className="grid grid-cols-2 gap-3">
              {PREDEFINED_SUBLOCATIONS.map(({ name, icon: Icon }) => (
                <Card
                  key={name}
                  className="p-4 hover:border-primary cursor-pointer transition-colors"
                  onClick={() => handleSelectPredefinedSublocation(name)}
                >
                  <div className="flex flex-col items-center justify-center text-center gap-2">
                    <div className="aspect-square bg-blue-50 flex items-center justify-center p-3 rounded-lg">
                      <Icon className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <h3 className="font-medium text-sm">{name}</h3>
                  </div>
                </Card>
              ))}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t" />
              <span className="text-xs uppercase text-muted-foreground">Or create custom</span>
              <div className="flex-1 border-t" />
            </div>

            {/* Custom sublocation form */}
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleCreateSublocation)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <Label htmlFor="name">
                        Sublocation Name <span className="text-red-500">*</span>
                      </Label>
                      <FormControl>
                        <Input
                          id="name"
                          placeholder="e.g., 7th Floor, West Wing"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
        }
        footer={
          <StepFooter
            actions={[
              <Button
                key="save"
                onClick={form.handleSubmit(handleCreateSublocation)}
                disabled={!form.formState.isValid || createSublocation.isPending}
                className="w-full"
                size="lg"
              >
                {createSublocation.isPending ? 'Saving...' : 'Save custom sublocation'}
              </Button>,
            ]}
          />
        }
      />
    )
  }

  return (
    <StepFrame
      header={
        <StepHeader
          title="Where is this equipment located?"
          description="Choose a sublocation so teams can find it quickly on site."
          onBack={goToPreviousStep}
        />
      }
      main={
        <SublocationList
          locationId={locationId}
          onSelect={handleSelectSublocation}
        />
      }
      footer={
        <StepFooter
          actions={[
            <Button key="add-sublocation" variant="outline" onClick={() => setShowForm(true)} className="w-full" size="lg">
              <Plus className="h-4 w-4 mr-2" />
              Add new sublocation
            </Button>
          ]}
        />
      }
    />
  )
}